import { useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { useSelector } from "react-redux";
import { getLocationHierarchy } from "../../global/utility/widgetUtils";
import {
  CHART_FONT_FAMILY,
  TOOLTIP_STYLE,
  chartTextStyle,
  buildChartTitle,
  buildLegend,
  buildSliderDataZoom,
  SLIDER_LABEL_EVENTS,
  CHART_GRID_BOTTOM,
  createChartToolbox,
} from "./ApacheChartUtils";

const ApacheHeatMatrixChart = ({
  title = "",
  xAxisData = [],
  tooltipXAxisData = [],
  series = [],
  height = "400px",
  loading = false,
  showLegend = false,
}) => {
  const chartRef = useRef(null);
  const siteTagList = useSelector((state) => state.sitesReducer.siteTagList);
  const option = useMemo(() => {
    const xData = xAxisData?.data ?? xAxisData;
    const yData = series.map((s) => s?.name ?? "");
    const yDataLocationHierarchy = series.map(
      (s, i) => (s?.locationId && getLocationHierarchy(siteTagList, s.locationId)) || yData[i]
    );

    const heatData = series.flatMap((s, yIdx) =>
      (s?.data ?? []).map((value, xIdx) => [xIdx, yIdx, value ?? "-"])
    );

    const allValues = heatData.map((d) => (Number.isFinite(d[2]) ? d[2] : 0));
    const minVal = allValues.length ? Math.min(...allValues) : 0;
    const rawMax = allValues.length ? Math.max(...allValues) : 1;
    // visualMap needs a non-empty range, otherwise ECharts feeds an undefined colour to addColorStop
    const maxVal = rawMax > minVal ? rawMax : minVal + 1;

    return {
      textStyle: chartTextStyle,
      title: buildChartTitle(title),
      tooltip: {
        trigger: "item",
        textStyle: { ...chartTextStyle, color: "#000000" },
        formatter: (params) => {
          const xIdx = params.data[0];
          const yIdx = params.data[1];
          const label = tooltipXAxisData[xIdx] ?? xData[xIdx] ?? params.data[0];
          const locationName = yData[yIdx] ?? "";
          const unit = xAxisData?.toolTipUnit ?? "";
          const valueSuffix = unit === "Percent" ? "%" : "";
          return `<div style="${TOOLTIP_STYLE}">${label}</div><div style="${TOOLTIP_STYLE}">${params.marker}${locationName}: <b>${params.data[2]}${valueSuffix}</b></div>`;
        },
      },
      legend: buildLegend(showLegend),
      toolbox: createChartToolbox(chartRef),
      grid: {
        left: 15,
        right: 55,
        bottom: CHART_GRID_BOTTOM,
        top: title ? 60 : 40,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xData,
        name: xAxisData?.xName ?? "Time",
        nameLocation: "middle",
        nameGap: 25,
        nameTextStyle: { fontWeight: "bold" },
        splitArea: { show: true },
        axisLabel: { rotate: 0, interval: "auto", fontFamily: CHART_FONT_FAMILY, fontSize: 12 },
      },
      yAxis: {
        type: "category",
        data: yData,
        name: xAxisData?.yName ?? "",
        nameLocation: "middle",
        nameGap: 60,
        nameRotate: 90,
        nameTextStyle: { fontWeight: "bold" },
        splitArea: { show: true },
        axisLabel: { fontFamily: CHART_FONT_FAMILY, fontSize: 12 },
        triggerEvent: true,
        tooltip: {
          show: true,
          textStyle: { ...chartTextStyle, color: "#000000" },
          formatter: (params) =>
            `<div style="${TOOLTIP_STYLE}">${yDataLocationHierarchy[params?.tickIndex] ?? params?.name ?? ""}</div>`,
        },
      },
      visualMap: {
        min: minVal,
        max: maxVal,
        calculable: true,
        orient: "vertical",
        right: 0,
        top: "center",
        padding: [0, 0, 0, 0],
        inRange: { color: ["#E3F2FD", "#003E6C"] },
        textStyle: { fontFamily: CHART_FONT_FAMILY, fontSize: 12 },
      },
      dataZoom: buildSliderDataZoom(0, "x"),
      series: [
        {
          name: series[0]?.name ?? "",
          type: "heatmap",
          data: heatData,
          label: { show: false },
          itemStyle: { borderColor: "#ffffff", borderWidth: 1 },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    };
  }, [title, xAxisData, tooltipXAxisData, series, showLegend, siteTagList]);

  return (
    <ReactECharts
      ref={chartRef}
      option={option}
      style={{ height }}
      showLoading={loading}
      notMerge
      lazyUpdate
      onEvents={SLIDER_LABEL_EVENTS}
    />
  );
};

export default ApacheHeatMatrixChart;
